import { forwardRef, Fragment } from 'react';
import cx from 'classnames';
import { take, uniqBy } from 'lodash';
import PropTypes from 'prop-types';

import { CustomTooltip } from 'src/components/CustomTooltip';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { GenerationToolIcon } from 'src/components/GenerationToolIcon';
import { getGenerationToolIcon } from 'src/components/GenerationToolIcon/utils';

import { groupToolsByResult } from './groupToolsByResult';
import { useStyles } from './useStyles';

export const GenerationToolsIcons = forwardRef(
  ({ className, tools, maxVisible, iconOverrides, groupByResult, ...restProps }, ref) => {
    const styles = useStyles();

    const visibleCount = maxVisible > 0 ? maxVisible : Number.MAX_SAFE_INTEGER;
    // Tools sharing an icon are drawn once, so an override decides that grouping as well: two tools
    // overridden to the same icon collapse into one, the way two tools with the same icon do.
    // With `groupByResult` the unit is the result instead (see `groupToolsByResult`): one icon per
    // result, each with its name in a tooltip.
    const results = groupByResult
      ? groupToolsByResult(tools)
      : uniqBy(tools, (tool) => iconOverrides[tool] || getGenerationToolIcon(tool)).map((tool) => ({
          tool,
        }));

    return (
      <div
        className={cx(styles.root, className)}
        ref={ref}
        {...restProps}
      >
        {take(results, visibleCount).map(({ tool, label }, i) => {
          const icon = (
            <GenerationToolIcon
              className={styles.icon}
              tool={tool}
              icon={iconOverrides[tool]}
            />
          );

          return label ? (
            <CustomTooltip
              key={tool + i}
              title={label}
            >
              <span className={styles.item}>{icon}</span>
            </CustomTooltip>
          ) : (
            <Fragment key={tool + i}>{icon}</Fragment>
          );
        })}

        {results.length > visibleCount && (
          <CustomTypography variant={TYPOGRAPHY_VARIANT.regular12}>
            +{results.length - visibleCount}
          </CustomTypography>
        )}
      </div>
    );
  }
);

GenerationToolsIcons.propTypes = {
  className: PropTypes.string,
  tools: PropTypes.arrayOf(PropTypes.string),
  maxVisible: PropTypes.number,
  iconOverrides: PropTypes.objectOf(PropTypes.elementType),
  groupByResult: PropTypes.bool,
};

GenerationToolsIcons.defaultProps = {
  className: null,
  tools: [],
  maxVisible: null,
  iconOverrides: {},
  groupByResult: false,
};
